import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SearchIcon, UserIcon, MenuIcon, ShoppingCartIcon, HeartIcon, LibraryIcon, BookIcon, StoreIcon, PlusIcon } from 'lucide-react';
import { useAfterTideUser } from '../../context/AfterTideUserContext';
import { useUserRole } from '../../context/UserRoleContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
export const AfterTideHeader = () => {
  const {
    cart,
    wishlist,
    readingList
  } = useAfterTideUser();
  const {
    isReader,
    toggleUserRole
  } = useUserRole();
  const location = useLocation();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const libraryCount = Object.keys(readingList).length;
  const isActive = (path: string) => location.pathname === path;
  const navLinks = [{
    path: '/aftertide',
    label: 'Home'
  }, {
    path: '/aftertide/explore',
    label: 'Browse'
  }, {
    path: '/aftertide/community',
    label: 'Community'
  }, {
    path: '/aftertide/series',
    label: 'Series'
  }, {
    path: '/aftertide/authors',
    label: 'Authors'
  }];
  return <header className="sticky top-0 z-50 bg-[#1a1a2e] border-b border-[#16213e] shadow-md hidden md:block" data-id="element-788" data-mp="22cowqv7utoyl">
      <style data-id="element-789" data-mp="27cjm2ubze6hj">
        {`
          @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400&display=swap');
          .font-playfair { font-family: 'Playfair Display', serif; }
        `}
      </style>

      {/* Top Bar */}
      <div className="border-b border-[#16213e]" data-id="element-790" data-mp="1jmz0e3og0v4k">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-791" data-mp="1jl5tcn9x48w1">
          <div className="grid grid-cols-3 items-center h-16" data-id="element-792" data-mp="1odp5dfsyivyq">
            {/* Left: Logo */}
            <div className="flex items-center" data-id="element-793" data-mp="qyl2dnxctyt0">
              <Link to="/aftertide" className="font-playfair text-2xl font-bold text-[#e2b04a] tracking-wide hover:opacity-90 transition-opacity" data-id="element-794" data-mp="qxrtagaxax7p">
                AfterTide
              </Link>
            </div>

            {/* Center: Search */}
            <div className="flex justify-center items-center" data-id="element-795" data-mp="1u8p30b138r1c">
              <div className="w-full max-w-md relative" data-id="element-796" data-mp="280lss8faaouk">
                <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[#a0a0b0]" data-id="element-797" data-mp="2826lrtly9zqb" />
                <input type="text" placeholder="Search dark fiction, authors, tropes..." className="w-full pl-10 pr-4 py-2 bg-[#16213e] border border-[#1a1a2e] text-[#f5f0e8] placeholder-[#a0a0b0] rounded-full focus:outline-none focus:ring-2 focus:ring-[#e2b04a] transition-colors text-sm" data-id="element-798" data-mp="1g969oocxrk93" />
              </div>
            </div>

            {/* Right: Actions */}
            <div className="flex justify-end items-center gap-5" data-id="element-799" data-mp="30o8462ur6xrz">
              {/* Role Toggle */}
              <button onClick={toggleUserRole} className="flex items-center px-2.5 py-1.5 rounded-full border border-[#16213e] hover:border-[#e2b04a]/30 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#e2b04a]" aria-label={`Switch to ${isReader ? 'vendor' : 'reader'} mode`} data-id="element-800" data-mp="2vown0sqh5q9v">
                <div className="flex items-center space-x-1" data-id="element-801" data-mp="2z7c8hsljga61">
                  <div className={`rounded-full p-1 transition-colors ${isReader ? 'bg-[#e2b04a]/20 text-[#e2b04a]' : 'bg-[#16213e] text-[#a0a0b0]'}`} data-id="element-802" data-mp="2z9ygtug96tpb">
                    <BookIcon className="h-3.5 w-3.5" data-id="element-803" data-mp="3rvq5q64sz2gj" />
                  </div>
                  <div className={`rounded-full p-1 transition-colors ${!isReader ? 'bg-[#c77dba]/20 text-[#c77dba]' : 'bg-[#16213e] text-[#a0a0b0]'}`} data-id="element-804" data-mp="3d6u2qnhlsfms">
                    <StoreIcon className="h-3.5 w-3.5" data-id="element-805" data-mp="9q4t2smq4psw" />
                  </div>
                </div>
                <span className="ml-2 text-xs font-medium text-[#f5f0e8]" data-id="element-806" data-mp="eiesr0dn548d">
                  {isReader ? 'Reader' : 'Vendor'}
                </span>
              </button>

              <div className="h-6 w-px bg-[#16213e]" data-id="element-807" data-mp="2hz3727tnvwim" />

              {/* Icons */}
              <div className="flex items-center gap-3" data-id="element-808" data-mp="2d09t6eagq3hx">
                <Link to="/aftertide/wishlist" className="relative p-1.5 text-[#a0a0b0] hover:text-[#e2b04a] transition-colors" aria-label="Wishlist" data-id="element-809" data-mp="2d11m1hcodn4i">
                  <HeartIcon className="h-5 w-5" data-id="element-810" data-mp="35r08d4zhj65h" />
                  {wishlist.length > 0 && <span className="absolute -top-1 -right-1 bg-[#e2b04a] text-[#1a1a2e] text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center" data-id="element-811" data-mp="30sgjavplhe8w">
                      {wishlist.length}
                    </span>}
                </Link>

                <Link to="/aftertide/library" className="relative p-1.5 text-[#a0a0b0] hover:text-[#e2b04a] transition-colors" aria-label="Library" data-id="element-812" data-mp="3oh9m9c4mwmd0">
                  <LibraryIcon className="h-5 w-5" data-id="element-813" data-mp="3r2hamew5mbny" />
                  {libraryCount > 0 && <span className="absolute -top-1 -right-1 bg-[#e2b04a] text-[#1a1a2e] text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center" data-id="element-814" data-mp="3w1agpm9430e3">
                      {libraryCount}
                    </span>}
                </Link>

                <Link to="/cart" className="relative p-1.5 text-[#a0a0b0] hover:text-[#e2b04a] transition-colors" aria-label="Cart" data-id="element-815" data-mp="dt1qsyhju8nr">
                  <ShoppingCartIcon className="h-5 w-5" data-id="element-816" data-mp="njwbhyf5d073" />
                  {cart.length > 0 && <span className="absolute -top-1 -right-1 bg-[#e2b04a] text-[#1a1a2e] text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center" data-id="element-817" data-mp="shpiuus6s08y">
                      {cart.length}
                    </span>}
                </Link>

                <button className="ml-1 p-1.5 text-[#a0a0b0] hover:text-[#e2b04a] transition-colors rounded-full border border-[#16213e] hover:border-[#e2b04a]/30 focus:outline-none focus:ring-2 focus:ring-[#e2b04a]" aria-label="User Profile" data-id="element-818" data-mp="2ykuurzowj2or">
                  <UserIcon className="h-4 w-4" data-id="element-819" data-mp="32kc2kzrswbx3" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Nav Bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-820" data-mp="3cbgf7su07kor">
        <nav className="flex space-x-8 h-12" data-id="element-821" data-mp="3cdaryjv4g6hr">
          {navLinks.map(item => {
          const active = isActive(item.path);
          return <Link key={item.path} to={item.path} className={`inline-flex items-center text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${active ? 'border-[#e2b04a] text-[#e2b04a]' : 'border-transparent text-[#a0a0b0] hover:text-[#f5f0e8] hover:border-[#e2b04a]/50'}`} data-id="element-822" data-mp="3m94dgsqtjabo">
                {item.label}
              </Link>;
        })}
        </nav>
      </div>
    </header>;
};